import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getServiceDetail, updateService, uploadServiceImages } from "apis";
import { useAppModal } from "components/common/AppModalProvider";
import { compressImage } from "utils/imageCompressor";
import { ServiceCategory } from "types";
import { PageHeader, Loading } from "components";

interface ScheduleInput {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
}

interface ImageItem {
  id: number;
  file?: File;
  url?: string;
  preview: string;
  isMain: boolean;
}

let scheduleIdCounter = 200;
let imageIdCounter = 200;

const categories: { icon: string; label: string; value: ServiceCategory }[] = [
  { icon: "💬", label: "수다·티타임", value: ServiceCategory.CHAT },
  { icon: "🎮", label: "게임·듀오", value: ServiceCategory.GAME },
  { icon: "🔮", label: "타로·운세", value: ServiceCategory.TAROT },
  { icon: "💡", label: "고민·피드백", value: ServiceCategory.FEEDBACK },
  { icon: "💻", label: "랜선 스터디", value: ServiceCategory.STUDY },
  { icon: "✨", label: "자유주제", value: ServiceCategory.FREE },
];

const ServiceEditForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showAlert } = useAppModal();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [category, setCategory] = useState<ServiceCategory>(ServiceCategory.FREE);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<ImageItem[]>([]);
  const [price, setPrice] = useState<number>(0);
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const [externalLink, setExternalLink] = useState("");
  const [schedules, setSchedules] = useState<ScheduleInput[]>([]);

  const updateSchedule = useCallback((scheduleId: number, field: keyof ScheduleInput, value: string) => {
    setSchedules(prev => prev.map(s => s.id === scheduleId ? { ...s, [field]: value } : s));
  }, []);

  const removeSchedule = useCallback((scheduleId: number) => {
    setSchedules(prev => prev.filter(s => s.id !== scheduleId));
  }, []);

  const checkOverlap = useCallback((date: string, startTime: string, endTime: string, excludeId?: number): boolean => {
    const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const newStart = toMin(startTime);
    const newEnd = toMin(endTime);
    return schedules.some(s => {
      if (s.date !== date) return false;
      if (excludeId !== undefined && s.id === excludeId) return false;
      const sStart = toMin(s.startTime);
      const sEnd = toMin(s.endTime);
      return newStart < sEnd && newEnd > sStart;
    });
  }, [schedules]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        const res = await getServiceDetail(id);
        const svc = res.data.data;
        setCategory(svc.category);
        setTitle(svc.title);
        setDescription(svc.description || "");
        setPrice(svc.price);
        setDurationMinutes(svc.durationMinutes || 60);
        setExternalLink("");
        if (svc.images && svc.images.length > 0) {
          setImages(svc.images.map((url, idx) => ({
            id: ++imageIdCounter,
            url,
            preview: url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL}/${url}`,
            isMain: idx === 0,
          })));
        }
        if (svc.schedules && svc.schedules.length > 0) {
          setSchedules(svc.schedules.filter(s => !s.isBooked).map(s => ({
            id: ++scheduleIdCounter,
            date: s.date,
            startTime: s.startTime,
            endTime: s.endTime,
          })));
        }
      } catch (err) {
        console.error(err);
        showAlert("서비스 정보를 불러올 수 없습니다.", { variant: 'error' });
        navigate("/mypage?tab=listings");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFiles = Array.from(e.target.files || []);
    const compressedFiles = await Promise.all(rawFiles.map(f => compressImage(f)));
    const newImages: ImageItem[] = compressedFiles.map(file => ({
      id: ++imageIdCounter,
      file,
      preview: URL.createObjectURL(file),
      isMain: false,
    }));
    setImages(prev => {
      const updated = [...prev, ...newImages].slice(0, 10);
      if (!updated.some(img => img.isMain) && updated.length > 0) updated[0].isMain = true;
      return updated;
    });
    if (imageInputRef.current) imageInputRef.current.value = "";
  }, []);

  const removeImage = useCallback((imgId: number) => {
    setImages(prev => {
      const filtered = prev.filter(img => img.id !== imgId);
      if (filtered.length > 0 && !filtered.some(img => img.isMain)) filtered[0].isMain = true;
      return filtered;
    });
  }, []);

  const setMainImage = useCallback((imgId: number) => {
    setImages(prev => prev.map(img => ({ ...img, isMain: img.id === imgId })));
  }, []);

  const isFormValid = category && title.trim().length > 0 && price > 0;

  const handleSubmit = async () => {
    if (!isFormValid || !id) return;
    setIsSubmitting(true);
    try {
      const newFiles = images.filter(img => img.file).map(img => img.file!);
      let newPaths: string[] = [];
      if (newFiles.length > 0) newPaths = await uploadServiceImages(newFiles);

      const sortedImages = [...images].sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0));
      const allPaths: string[] = [];
      let newIdx = 0;
      for (const img of sortedImages) {
        if (img.file) allPaths.push(newPaths[newIdx++]);
        else if (img.url) allPaths.push(img.url);
      }

      const scheduleData = schedules.map(s => ({ date: s.date, startTime: s.startTime, endTime: s.endTime }));
      await updateService(id, title, description, price, category, externalLink, durationMinutes, allPaths, scheduleData);
      showAlert("수정 완료! ✏️", { variant: 'success', icon: '✏️' });
      navigate("/mypage?tab=listings");
    } catch (error) {
      console.error(error);
      showAlert("수정 중 오류가 발생했습니다.", { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return (
    <div className="animate-fadeUp">
      <PageHeader title="서비스 수정" isArrow />
      <div className="bg-white rounded-2xl p-6"><Loading /></div>
    </div>
  );

  return (
    <div className="animate-fadeUp mobile:pb-[56px]">
      <PageHeader title="서비스 수정" isArrow />
      <div className="bg-white rounded-2xl p-6 mobile:rounded-none">
        {/* Category */}
        <div className="mb-6">
          <label className="text-[13px] font-bold text-achromatic-700 mb-2 block">카테고리</label>
          <div className="grid grid-cols-3 gap-2">
            {categories.map(c => (
              <button key={c.value} onClick={() => setCategory(c.value)}
                className={`py-2.5 rounded-xl text-[13px] font-semibold transition-all ${
                  category === c.value
                    ? "bg-primary-500 text-white shadow-md shadow-primary-500/20"
                    : "bg-achromatic-50 text-achromatic-500 hover:bg-achromatic-100"
                }`}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div className="mb-5">
          <label className="text-[13px] font-bold text-achromatic-700 mb-2 block">서비스명</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="서비스 이름을 입력하세요"
            className="w-full px-4 py-3 rounded-xl border border-achromatic-200 text-sm focus:border-primary-500 focus:outline-none transition-colors" />
        </div>

        {/* Description */}
        <div className="mb-5">
          <label className="text-[13px] font-bold text-achromatic-700 mb-2 block">서비스 설명</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="서비스에 대해 자세히 설명해주세요" rows={4}
            className="w-full px-4 py-3 rounded-xl border border-achromatic-200 text-sm focus:border-primary-500 focus:outline-none transition-colors resize-none" />
        </div>

        {/* Images */}
        <div className="mb-5">
          <label className="text-[13px] font-bold text-achromatic-700 mb-2 block">사진 ({images.length}/10)</label>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            <button onClick={() => imageInputRef.current?.click()}
              className="w-[80px] h-[80px] flex-shrink-0 rounded-xl border-2 border-dashed border-achromatic-200 flex flex-col items-center justify-center gap-0.5 text-achromatic-400 hover:border-primary-400 hover:text-primary-500 transition-colors">
              <span className="text-xl">+</span>
              <span className="text-[10px]">사진 추가</span>
            </button>
            <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} />
            {images.map(img => (
              <div key={img.id} className="w-[80px] h-[80px] flex-shrink-0 rounded-xl overflow-hidden relative group">
                <img src={img.preview} alt="" className="w-full h-full object-cover" />
                {img.isMain && <div className="absolute top-1 left-1 bg-primary-500 text-white text-[8px] px-1.5 py-0.5 rounded font-bold">대표</div>}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  {!img.isMain && <button onClick={() => setMainImage(img.id)} className="w-6 h-6 bg-white/90 rounded-full text-[10px]">⭐</button>}
                  <button onClick={() => removeImage(img.id)} className="w-6 h-6 bg-red-500/90 text-white rounded-full text-[10px]">✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Price & Duration */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="text-[13px] font-bold text-achromatic-700 mb-2 block">가격 (원)</label>
            <input type="number" value={price || ""} onChange={e => setPrice(Number(e.target.value))} placeholder="10000"
              className="w-full px-4 py-3 rounded-xl border border-achromatic-200 text-sm focus:border-primary-500 focus:outline-none" />
          </div>
          <div>
            <label className="text-[13px] font-bold text-achromatic-700 mb-2 block">시간 (분)</label>
            <input type="number" value={durationMinutes || ""} onChange={e => setDurationMinutes(Number(e.target.value))} placeholder="60"
              className="w-full px-4 py-3 rounded-xl border border-achromatic-200 text-sm focus:border-primary-500 focus:outline-none" />
          </div>
        </div>

        {/* External link */}
        <div className="mb-6">
          <label className="text-[13px] font-bold text-achromatic-700 mb-2 block">접속 링크 (Zoom/디스코드 등)</label>
          <input type="url" value={externalLink} onChange={e => setExternalLink(e.target.value)} placeholder="https://zoom.us/j/..."
            className="w-full px-4 py-3 rounded-xl border border-achromatic-200 text-sm focus:border-primary-500 focus:outline-none" />
          <p className="text-[11px] text-achromatic-400 mt-1">비어있으면 기존 링크가 유지됩니다</p>
        </div>

        {/* ── Schedule Management ── */}
        <div className="mb-6">
          <label className="text-[13px] font-bold text-achromatic-700 mb-2 block">
            판매 시간대 ({schedules.length}개)
          </label>
          <p className="text-[11px] text-achromatic-400 mb-3">날짜를 선택하면 시간대가 추가됩니다 (같은 날 여러 시간 가능)</p>

          {/* Date selector */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide mb-3">
            {Array.from({ length: 14 }, (_, i) => {
              const d = new Date();
              d.setDate(d.getDate() + i);
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              const dateStr = `${yyyy}-${mm}-${dd}`;
              const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
              const slotsForDay = schedules.filter(s => s.date === dateStr).length;
              return (
                <button key={`${dateStr}-${i}`}
                  onClick={() => {
                    const startTime = '10:00';
                    const endMin = 10 * 60 + durationMinutes;
                    const endTime = `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
                    if (checkOverlap(dateStr, startTime, endTime)) {
                      showAlert(`${d.getMonth() + 1}/${d.getDate()} 10시에 이미 다른 일정이 있습니다.`, { variant: 'warning' });
                      return;
                    }
                    setSchedules(prev => [...prev, { id: ++scheduleIdCounter, date: dateStr, startTime, endTime }]);
                  }}
                  className={`flex-shrink-0 flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl text-center transition-all min-w-[48px] relative ${
                    slotsForDay > 0 ? 'bg-primary-500 text-white shadow-sm'
                    : i === 0 ? 'bg-primary-50 text-primary-600 border border-primary-200'
                    : 'bg-achromatic-50 text-achromatic-600 hover:bg-achromatic-100'
                  }`}
                >
                  <span className="text-[10px] font-medium">{d.getMonth() + 1}월</span>
                  <span className="text-[15px] font-bold">{d.getDate()}</span>
                  <span className={`text-[10px] font-medium ${slotsForDay > 0 ? '' : d.getDay() === 0 ? 'text-red-400' : d.getDay() === 6 ? 'text-blue-400' : ''}`}>{dayNames[d.getDay()]}</span>
                  {slotsForDay > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-primary-500 rounded-full text-[9px] font-bold flex items-center justify-center shadow-sm">
                      {slotsForDay}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Schedule list */}
          {schedules.length > 0 && (
            <div className="flex flex-col gap-2 max-h-[350px] overflow-y-auto">
              {schedules.map(s => {
                const [y, m2, dayNum] = s.date.split('-').map(Number);
                const dateObj = new Date(y, m2 - 1, dayNum);
                const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
                return (
                  <div key={s.id} className="bg-achromatic-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[13px] font-bold text-achromatic-800">
                        {m2}/{dayNum} ({dayNames[dateObj.getDay()]})
                      </span>
                      <button onClick={() => removeSchedule(s.id)}
                        className="text-red-400 hover:text-red-600 transition-colors text-sm font-bold">✕</button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1 bg-white rounded-lg border border-achromatic-200 px-2 py-1.5">
                        <select value={parseInt(s.startTime.split(':')[0])}
                          onChange={e => {
                            const h = parseInt(e.target.value);
                            const mn = parseInt(s.startTime.split(':')[1]) || 0;
                            const st = `${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
                            const endMin = h * 60 + mn + durationMinutes;
                            const et = `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
                            if (checkOverlap(s.date, st, et, s.id)) {
                              showAlert('해당 시간에 이미 다른 일정이 있습니다.', { variant: 'warning' });
                              return;
                            }
                            updateSchedule(s.id, "startTime", st);
                            updateSchedule(s.id, "endTime", et);
                          }}
                          className="text-[13px] font-bold text-achromatic-800 bg-transparent focus:outline-none cursor-pointer"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>{i}시</option>
                          ))}
                        </select>
                        <span className="text-achromatic-400 font-bold">:</span>
                        <select value={parseInt(s.startTime.split(':')[1]) || 0}
                          onChange={e => {
                            const h = parseInt(s.startTime.split(':')[0]);
                            const mn = parseInt(e.target.value);
                            const st = `${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
                            const endMin = h * 60 + mn + durationMinutes;
                            const et = `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
                            if (checkOverlap(s.date, st, et, s.id)) {
                              showAlert('해당 시간에 이미 다른 일정이 있습니다.', { variant: 'warning' });
                              return;
                            }
                            updateSchedule(s.id, "startTime", st);
                            updateSchedule(s.id, "endTime", et);
                          }}
                          className="text-[13px] font-bold text-achromatic-800 bg-transparent focus:outline-none cursor-pointer"
                        >
                          {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                            <option key={m} value={m}>{String(m).padStart(2, '0')}분</option>
                          ))}
                        </select>
                      </div>
                      <span className="text-achromatic-400 text-sm">→</span>
                      <span className="text-[13px] font-bold text-primary-500 bg-primary-50 px-2.5 py-1.5 rounded-lg">{s.endTime}</span>
                      <span className="text-[11px] text-achromatic-400">({durationMinutes}분)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {schedules.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-6 text-achromatic-300 bg-achromatic-50 rounded-xl">
              <span className="text-2xl">📅</span>
              <span className="text-[12px] font-medium">위에서 날짜를 선택해주세요</span>
            </div>
          )}
        </div>

        {/* Submit */}
        <button onClick={handleSubmit} disabled={!isFormValid || isSubmitting}
          className={`w-full py-3.5 rounded-xl text-sm font-bold transition-all ${
            isFormValid && !isSubmitting
              ? "bg-primary-500 text-white hover:bg-primary-600 active:scale-[0.98]"
              : "bg-achromatic-100 text-achromatic-400 cursor-not-allowed"
          }`}>
          {isSubmitting ? "수정 중..." : "수정 완료"}
        </button>
      </div>
    </div>
  );
};

export default ServiceEditForm;
